<script setup>
import { ref, computed, onMounted } from "vue";
import { helpers, required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import Autocomplete from "@/components/employability/forms/elements/Autocomplete.vue";
import { useEmployabilityStore, useListStore, useUserStore } from "@/stores";
import { useRouter } from "vue-router";
import { formatDateToIsoString } from "@/helpers/helpers";

const listStore = useListStore();
const userStore = useUserStore();
const employabilityStore = useEmployabilityStore();

const router = useRouter();

const user = userStore.user;

const emplServices = computed(() => listStore.emplServices);
const branches = computed(() => listStore.branches);
const emplOfficers = computed(() => listStore.employabilityOfficers);

const submitForm = async () => {
  const isValid = await v$.value.$validate();
  form.value.createdUserId = user.id;

  const data = form.value;

  data.date = formatDateToIsoString(data.date);

  if (isValid) {
    employabilityStore.createEmployability(data);
  }
};

const rules = {
  beneficiaryIntake: {
    required: helpers.withMessage("Beneficiary is required", required),
  },
  branch: {
    required: helpers.withMessage("Branch is required", required),
  },
  service: {
    required: helpers.withMessage("Type of Service is required", required),
  },
  responsibleOfficer: {
    required: helpers.withMessage("Responsible User is required", required),
  },
  date: {
    required: helpers.withMessage("Date is required", required),
  },
};

const searchBeneficiaries = ref(null);
const beneficiaries = ref([]);

const isLoading = ref(false);

const onInput = async (val) => {
  if (val != null && val.length > 2) {
    isLoading.value = true;
    beneficiaries.value = await listStore.fetchBeneficiaryIntakes({
      keyword: val,
    });
    isLoading.value = false;
  } else {
    beneficiaries.value = [];
  }
};

const form = ref({
  beneficiaryIntake: null,
  branch: null,
  service: null,
  responsibleOfficer: null,
  date: null,
});

const v$ = useVuelidate(rules, form);

const isRequired = (field) => {
  return "required" in rules[field];
};

const getErrorMessages = (field) =>
  computed(() =>
    v$.value[field]?.$error
      ? v$.value[field].$errors.map((e) => e.$message)
      : []
  );

const handleAppendClick = () => {
  const route = router.resolve({
    name: "BeneficiaryView",
    params: { id: form.value.beneficiaryIntake.id },
  });

  window.open(route.href, "_blank");
};

onMounted(async () => {
  await listStore.fetchEmplServices();
  await listStore.fetchBranches();
  await listStore.fetchEmployabilityOfficers(true);
});
</script>

<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="6">
        <h1 class="text-employability">Add Employability Service</h1>
      </v-col>
      <v-col cols="6" class="d-flex justify-end">
        <v-btn color="employability" size="large" @click="submitForm">
          <v-icon icon="mdi-plus" /> Save new entry
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-form class="custom-form mt-5 d-flex flex-column">
          <v-autocomplete
            v-model="form.beneficiaryIntake"
            v-model:search-input="searchBeneficiaries"
            :loading="isLoading"
            return-object
            placeholder="Start typing to Search"
            hint="Hint: Type 3 or more characters to get results"
            persistent-hint
            item-title="name"
            item-value="id"
            hide-details="auto"
            :items="beneficiaries"
            variant="outlined"
            :menu-props="{ maxWidth: 0, closeOnContentClick: false }"
            :error-messages="getErrorMessages('beneficiaryIntake').value"
            :append-icon="form.beneficiaryIntake != null ? 'mdi-eye' : ''"
            @update:search="(val) => onInput(val)"
            @blur="v$.beneficiaryIntake.$touch()"
            @click:append="handleAppendClick"
          >
            <template #label>
              Beneficiary
              <span v-if="isRequired('beneficiaryIntake')" class="ml-1 red--text">
                <strong> *</strong>
              </span>
            </template>
          </v-autocomplete>

          <Autocomplete
            v-model="form.branch"
            label="Odyssea Branch"
            :items="branches"
            :multiple="false"
            :chips="false"
            :error-messages="getErrorMessages('branch').value"
            :is-required="isRequired('branch')"
            @blur="v$.branch.$touch()"
          />

          <v-select
            v-model="form.service"
            :items="emplServices"
            item-title="name"
            item-value="value"
            return-object
            variant="outlined"
            hide-details="auto"
            :error-messages="getErrorMessages('service').value"
            @blur="v$.service.$touch()"
          >
            <template #label>
              Type of Service
              <span v-if="isRequired('service')" class="red--text">
                <strong>* </strong>
              </span>
            </template>
          </v-select>

          <Autocomplete
            v-model="form.responsibleOfficer"
            label="Responsible"
            :items="emplOfficers"
            :multiple="false"
            :chips="false"
            :error-messages="getErrorMessages('responsibleOfficer').value"
            :is-required="isRequired('responsibleOfficer')"
            @blur="v$.responsibleOfficer.$touch()"
          />

          <v-date-input
            v-model="form.date"
            variant="outlined"
            prepend-icon=""
            placeholder="DD/MM/YYYY"
            prepend-inner-icon="$calendar"
            hide-details="auto"
            :error-messages="getErrorMessages('date').value"
            @blur="v$.date.$touch()"
          >
            <template #label>
              Date
              <span v-if="isRequired('date')" class="red--text">
                <strong>* </strong>
              </span>
            </template>
          </v-date-input>
        </v-form>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
h1 {
  color: rgb(var(--v-theme-employability));
}
.flex-50 {
  flex: 0 0 50%;
}
</style>
